import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField, Tooltip, ToggleButton, ToggleButtonGroup, IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { bloomberg } from '../theme/bloombergTheme';
import {
  getActivePortfolios, getModelPortfolios, createActiveVersion, Scorecard, ModelPortfolioSummary,
  REBALANCE_SOURCE_LABELS, isActiveSection,
} from '../services/api';
import YardstickPicker from './YardstickPicker';

/**
 * Active portfolios: every judgement-based model — re-weighted theme
 * versions, convictions, curated themes, sector & regional baskets — each
 * scored against a benchmark (a matching theme model where one exists,
 * otherwise an ETF). The headline is the real result since the weights were
 * set, because a backtest of weights chosen from the same history flatters
 * them. The question the section answers: is our judgement adding value?
 */

const MONO = bloomberg.font.mono;
const HEAD = bloomberg.font.heading;
const ACCENT = '#a855f7';
const YARD = '#94a3b8';
const eyebrow = { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: bloomberg.text.muted };
const card = { bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 2 };
const pct = (v?: number | null) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)}%`);
const tone = (v?: number | null, better: 'up' | 'down' = 'up') => {
  if (v === null || v === undefined || Math.abs(v) < 1e-9) return bloomberg.text.secondary;
  return (better === 'up' ? v > 0 : v < 0) ? bloomberg.status.positive : bloomberg.status.negative;
};
const sub = (a?: number | null, b?: number | null) => (a === null || a === undefined || b === null || b === undefined ? null : a - b);
const realDiff = (r: Scorecard) => (r.since_change && r.since_change.days >= 1 ? r.since_change.model_return - r.since_change.yardstick_return : null);
const shortDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });

const GROUPS: { kind: string; label: string; note: string }[] = [
  { kind: 'active', label: 'Re-weighted themes', note: 'A theme model re-weighted by the Efficient Frontier or by hand, against the equal-weight original' },
  { kind: 'conviction', label: 'Convictions', note: 'Personal books, against a broad market in their own currency' },
  { kind: 'curated_theme', label: 'Curated themes', note: 'Hand-picked themes, against the systematic theme model built from ETFs' },
  { kind: 'regional', label: 'Sector & regional', note: 'Stock baskets, against their sector or local market' },
];
const KIND_GROUP: Record<string, string> = {
  theme: 'Theme models', curated_theme: 'Curated themes', regional: 'Regional & sector', conviction: 'Convictions',
  allocation: 'Allocations', screen: 'Screens',
};

function Spark({ series }: { series: { model: number; yardstick: number }[] }) {
  if (series.length < 3) return <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography>;
  const w = 96, h = 28;
  const vals = series.flatMap((p) => [p.model, p.yardstick]);
  const lo = Math.min(...vals), hi = Math.max(...vals), span = hi - lo || 1;
  const x = (i: number) => 2 + (i / (series.length - 1)) * (w - 4);
  const y = (v: number) => h - 2 - ((v - lo) / span) * (h - 4);
  const line = (k: 'model' | 'yardstick') => series.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p[k])}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Portfolio and benchmark since the start date">
      <path d={line('yardstick')} fill="none" stroke={YARD} strokeWidth={1.3} />
      <path d={line('model')} fill="none" stroke={ACCENT} strokeWidth={1.8} />
    </svg>
  );
}

export default function ActivePortfolios() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Scorecard[] | null>(null);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [owner, setOwner] = useState<'all' | 'Marc' | 'Damien'>('all');
  const [editing, setEditing] = useState<Scorecard | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [bases, setBases] = useState<ModelPortfolioSummary[]>([]);
  const [base, setBase] = useState<ModelPortfolioSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = () => getActivePortfolios()
    .then((d) => { setRows(d.portfolios); setAsOf(d.as_of); })
    .catch(() => setError('Could not load the active portfolios.'));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!newOpen || bases.length) return;
    getModelPortfolios().then((all) => setBases(all.filter((p) => !isActiveSection(p.kind) && p.holdings_count > 0))).catch(() => {});
  }, [newOpen, bases.length]);

  const visible = useMemo(() => (rows || []).filter((r) => owner === 'all' || r.owner === owner), [rows, owner]);
  const byDiff = (a: Scorecard, b: Scorecard) => {
    const x = realDiff(a), y = realDiff(b);
    if (x === null && y === null) return a.name.localeCompare(b.name);
    if (x === null) return 1;
    if (y === null) return -1;
    return y - x;
  };
  // Backdated test records are shown, but kept out of the totals: they aren't real.
  const live = visible.filter((r) => realDiff(r) !== null && !r.start.test && !r.start.walk_forward);
  const beating = live.filter((r) => (realDiff(r) || 0) > 0).length;
  const avg = live.length ? live.reduce((a, r) => a + (realDiff(r) || 0), 0) / live.length : null;
  const approx = visible.filter((r) => !r.start.exact).length;

  const open = (id: number) => navigate(`/portfolio/active?id=${id}`);
  const create = async () => {
    if (!base) return;
    setCreating(true); setCreateError(null);
    try {
      const child = await createActiveVersion(base.id);
      open(child.id);
    } catch (err: any) {
      setCreateError(err?.response?.data?.detail || 'Could not create the active portfolio.');
      setCreating(false);
    }
  };

  const th: React.CSSProperties = { textAlign: 'right', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: bloomberg.text.muted, whiteSpace: 'nowrap', borderBottom: `1px solid ${bloomberg.border.secondary}` };
  const td: React.CSSProperties = { textAlign: 'right', padding: '9px 10px', fontFamily: MONO, fontSize: 12.5, whiteSpace: 'nowrap', borderBottom: `1px solid ${bloomberg.border.primary}` };
  const COLS = 11;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography sx={{ ...eyebrow, color: ACCENT, mb: 0.25 }}>Active portfolios</Typography>
          <Typography sx={{ fontFamily: HEAD, fontSize: 22, fontWeight: 700, color: bloomberg.text.primary, lineHeight: 1.2 }}>
            Is our judgement adding value?
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: bloomberg.text.muted, mt: 0.25, maxWidth: 780 }}>
            Every hand-built or re-weighted portfolio against a fair benchmark: the matching systematic theme model
            where one exists, otherwise its sector or local market. Judge on the real record; the backtest is for reference.
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={owner} onChange={(_, v) => v && setOwner(v)}>
          <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: 12, px: 1.5 }}>All</ToggleButton>
          <ToggleButton value="Marc" sx={{ textTransform: 'none', fontSize: 12, px: 1.5 }}>Marc</ToggleButton>
          <ToggleButton value="Damien" sx={{ textTransform: 'none', fontSize: 12, px: 1.5 }}>Damien</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" disableElevation onClick={() => { setNewOpen(true); setBase(null); setCreateError(null); }}
          sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT, filter: 'brightness(1.1)' } }}>
          + New active portfolio
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {!rows && !error && <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>}

      {rows && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25, mb: 2 }}>
            {([
              ['Portfolios', String(visible.length), owner === 'all' ? null : `Owned by ${owner}`],
              ['Beating their benchmark', live.length ? `${beating} of ${live.length}` : '—', 'Real record, since the start date'],
              ['Average difference', pct(avg), 'Portfolio minus benchmark'],
              ['Prices to', asOf ? new Date(`${asOf}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—',
                approx ? `${approx} tracked from creation (approx.)` : null],
            ] as [string, string, string | null][]).map(([k, v, note]) => (
              <Box key={k} sx={{ ...card, p: 1.5 }}>
                <Typography sx={eyebrow}>{k}</Typography>
                <Typography sx={{ fontFamily: HEAD, fontSize: 22, fontWeight: 700, color: k === 'Average difference' ? tone(avg) : bloomberg.text.primary, lineHeight: 1.3 }}>{v}</Typography>
                {note && <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>{note}</Typography>}
              </Box>
            ))}
          </Box>

          <Box sx={{ ...card, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', color: bloomberg.text.primary }}>
              <thead>
                <tr>
                  <th scope="col" style={{ ...th, textAlign: 'left' }}>Portfolio</th>
                  <th scope="col" style={{ ...th, textAlign: 'left' }}>Benchmark</th>
                  <th scope="col" style={{ ...th, textAlign: 'left' }}>Since</th>
                  <th scope="col" style={th}>Days</th>
                  <th scope="col" style={{ ...th, color: ACCENT }}>Portfolio</th>
                  <th scope="col" style={{ ...th, color: YARD }}>Benchmark</th>
                  <th scope="col" style={{ ...th, color: bloomberg.text.primary }}>Difference</th>
                  <th scope="col" style={{ ...th, textAlign: 'left' }}>Path</th>
                  <th scope="col" style={{ ...th, borderLeft: `1px solid ${bloomberg.border.primary}` }} title="Today's weights run over the past year, minus the benchmark. In-sample.">Backtest 1Y Δ</th>
                  <th scope="col" style={th} title="In-sample">Sharpe Δ</th>
                  <th scope="col" style={th} title="In-sample; lower is better">Vol Δ</th>
                </tr>
              </thead>
              {GROUPS.map((g) => {
                const list = visible.filter((r) => r.kind === g.kind).sort(byDiff);
                if (!list.length) return null;
                const gLive = list.filter((r) => realDiff(r) !== null && !r.start.test && !r.start.walk_forward);
                const gBeat = gLive.filter((r) => (realDiff(r) || 0) > 0).length;
                return (
                  <tbody key={g.kind}>
                    <tr>
                      <td colSpan={COLS} style={{ padding: '14px 10px 6px', borderBottom: `1px solid ${bloomberg.border.secondary}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography sx={{ ...eyebrow, color: ACCENT, fontSize: 11 }}>{g.label} · {list.length}</Typography>
                          {gLive.length > 0 && <Typography sx={{ fontSize: 12, color: bloomberg.text.secondary }}>{gBeat} of {gLive.length} beating their benchmark</Typography>}
                          <Typography sx={{ fontSize: 11.5, color: bloomberg.text.muted }}>{g.note}</Typography>
                        </Box>
                      </td>
                    </tr>
                    {list.map((r) => {
                      const s = r.since_change;
                      const liveRow = !!s && s.days >= 1;
                      const dReal = realDiff(r);
                      const m = r.backtest.model, b = r.backtest.yardstick;
                      const d1y = sub(m.returns?.['1Y'], b.returns?.['1Y']);
                      const dSh = sub(m.sharpe, b.sharpe);
                      const dVol = sub(m.vol, b.vol);
                      const lc = r.last_change;
                      return (
                        <tr key={r.id} onClick={() => open(r.id)} style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bb-bg-hover)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                          <th scope="row" style={{ ...td, textAlign: 'left', fontFamily: 'inherit', whiteSpace: 'normal', minWidth: 210 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: bloomberg.text.primary }}>
                              {r.name}
                              {r.owner && <Box component="span" sx={{ ml: 0.75, px: 0.6, py: 0.1, borderRadius: 0.5, fontSize: 10.5, fontWeight: 700, bgcolor: `${ACCENT}22`, color: ACCENT }}>{r.owner}</Box>}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: bloomberg.text.muted }}>
                              {r.n_holdings} names{r.top_weight ? ` · top ${(r.top_weight * 100).toFixed(0)}%` : ''}
                              {lc ? ` · ${REBALANCE_SOURCE_LABELS[lc.source] || lc.source}${lc.note ? `: ${lc.note}` : ''}` : ''}
                            </Typography>
                          </th>
                          <td style={{ ...td, textAlign: 'left', fontFamily: 'inherit', whiteSpace: 'normal', minWidth: 170 }} onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={r.yardstick?.full || ''} placement="top-start">
                                <Typography sx={{ fontSize: 12.5, color: r.yardstick ? bloomberg.text.primary : bloomberg.text.muted, flex: 1 }}>
                                  {r.yardstick ? r.yardstick.label : 'Not set'}
                                </Typography>
                              </Tooltip>
                              <Tooltip title="Change benchmark">
                                <IconButton size="small" onClick={() => setEditing(r)} aria-label={`Change benchmark for ${r.name}`} sx={{ color: bloomberg.text.muted }}>
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </td>
                          <td style={{ ...td, textAlign: 'left', fontFamily: 'inherit', fontSize: 12 }}>
                            {r.start.date ? shortDate(r.start.date) : '—'}
                            {r.start.test && (
                              <Tooltip title="Backdated for testing: the weights were chosen with later prices, so this isn't a real record. Left out of the totals.">
                                <Box component="span" sx={{ ml: 0.75, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(245,158,11,0.18)', color: '#f59e0b', cursor: 'help' }}>test</Box>
                              </Tooltip>
                            )}
                            {r.start.walk_forward && (
                              <Tooltip title="Walk-forward test: the weights were chosen using only prices up to this date, then tracked. A fair simulation, but not a decision taken at the time, so it's left out of the totals.">
                                <Box component="span" sx={{ ml: 0.75, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(59,130,246,0.18)', color: '#60a5fa', cursor: 'help' }}>walk-forward</Box>
                              </Tooltip>
                            )}
                            {!r.start.exact && (
                              <Tooltip title="Tracked from creation: the weight log began in Aug 2026, so earlier edits aren't recorded.">
                                <Box component="span" sx={{ ml: 0.75, px: 0.5, borderRadius: 0.5, fontSize: 10, border: `1px solid ${bloomberg.border.secondary}`, color: bloomberg.text.muted, cursor: 'help' }}>approx.</Box>
                              </Tooltip>
                            )}
                          </td>
                          <td style={td}>{s ? s.days : '—'}</td>
                          <td style={td}>{liveRow ? pct(s!.model_return) : '—'}</td>
                          <td style={{ ...td, color: bloomberg.text.secondary }}>{liveRow ? pct(s!.yardstick_return) : '—'}</td>
                          <td style={{ ...td, fontWeight: 700, fontSize: 13.5, color: tone(dReal) }}>
                            {dReal !== null ? pct(dReal) : s ? (
                              <Tooltip title="Weights set today: the real comparison starts from the next close.">
                                <span style={{ color: bloomberg.text.muted, fontWeight: 400, fontSize: 12 }}>starts next close</span>
                              </Tooltip>
                            ) : <span style={{ color: bloomberg.text.muted, fontWeight: 400 }}>—</span>}
                          </td>
                          <td style={{ ...td, textAlign: 'left' }}><Spark series={s?.series || []} /></td>
                          <td style={{ ...td, color: bloomberg.text.muted, borderLeft: `1px solid ${bloomberg.border.primary}` }}>{pct(d1y)}</td>
                          <td style={{ ...td, color: bloomberg.text.muted }}>{dSh === null ? '—' : `${dSh >= 0 ? '+' : '−'}${Math.abs(dSh).toFixed(2)}`}</td>
                          <td style={{ ...td, color: bloomberg.text.muted }}>{pct(dVol)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                );
              })}
            </table>
            {visible.length === 0 && (
              <Typography sx={{ p: 3, fontSize: 13, color: bloomberg.text.muted }}>No portfolios {owner === 'all' ? 'yet' : `owned by ${owner}`}.</Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: bloomberg.text.muted, mt: 1, lineHeight: 1.55, maxWidth: 1000 }}>
            Each group is sorted by the real difference. The real record starts when the current weights were set; portfolios
            marked approx. are tracked from creation, since the weight log only began in Aug 2026. The greyed backtest
            columns run today's weights over the past year and flatter weights chosen from that same history. Returns are in
            each stock's own currency, so prefer benchmarks listed in the portfolio's currency. Under 20 trading days is too
            early to judge.
          </Typography>
        </>
      )}

      {editing && (
        <YardstickPicker open={!!editing} portfolioId={editing.id} portfolioName={editing.name}
          current={editing.yardstick?.ref ?? null} onClose={() => setEditing(null)} onSaved={() => load()} />
      )}

      <Dialog open={newOpen} onClose={() => !creating && setNewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>New active portfolio</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: bloomberg.text.secondary, mb: 2 }}>
            Choose the model to start from. You get a copy with the same names and weights, judged against the
            original; the original stays as it is.
          </Typography>
          <Autocomplete
            options={[...bases].sort((a, b) =>
              (a.kind === 'theme' ? 0 : 1) - (b.kind === 'theme' ? 0 : 1) || (KIND_GROUP[a.kind || ''] || 'Other').localeCompare(KIND_GROUP[b.kind || ''] || 'Other') || a.name.localeCompare(b.name))}
            groupBy={(o) => KIND_GROUP[o.kind || ''] || 'Other'}
            getOptionLabel={(o) => o.name}
            value={base}
            onChange={(_, v) => setBase(v)}
            loading={!bases.length}
            renderInput={(params) => <TextField {...params} label="Start from" size="small" autoFocus />}
          />
          <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, mt: 1.5 }}>
            Next: on its Efficient Frontier tab, apply a strategy, or edit the weights by hand.
          </Typography>
          {createError && <Alert severity="error" sx={{ mt: 1.5 }}>{createError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={!base || creating}>{creating ? 'Creating…' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
